import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../hooks/useOrganization';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Terminal, 
  BarChart3, 
  FileTextIcon, 
  Target,
  Upload,
  History,
  Clock,
  FileEdit,
  Users,
  ChevronRight,
  Brain,
  Pencil,
  Check,
  X,
  Plus,
  ArrowLeft,
  FolderOpen
} from 'lucide-react';

import { DashboardHeader } from './DashboardHeader';
import { UploadForm } from '../upload/UploadForm';
import { AnalysisHistory } from '../analysis/AnalysisHistory';
import { RecentActivity } from './RecentActivity';
import { JobDescriptionGenerator } from './JobDescriptionGenerator';
import { DashboardWorkflowHelper } from './DashboardWorkflowHelper';
import { DashboardErrorBoundary } from './DashboardErrorBoundary';
import { useToast } from '../../hooks/useToast';
import { useJobDescriptions } from './hooks/useJobDescriptions';
import { useSkillsAssessments } from '../../hooks/useSkillsAssessments';
import { useUserActions } from '../../hooks/useUserActions';
import { RecentJobDescriptions } from './RecentJobDescriptions';
import { RecentInterviews } from './RecentInterviews';
import { RecentSkillsAssessments } from './RecentSkillsAssessments';
import { listAnalyses } from '../../services/analysis/queries';
import { supabase } from '../../lib/supabase';
import { GDPRCompliance } from '../common/GDPRCompliance';

import { SkillsAssessment } from './ai-features/SkillsAssessment';
import { BulkAnalysisPage } from '../analysis/BulkAnalysisPage';
import { InterviewDashboard } from '../interview/InterviewDashboard';
import { ProjectModal } from '../modals/ProjectModal';
import { MarketIntelligenceOverlay } from './MarketIntelligenceOverlay';

/**
 * Dashboard Component - Project-Centric Hiring Management Platform
 * 
 * This dashboard is organized around projects as the main organizing feature:
 * 
 * 1. **Project Selection**: Users can select a specific project or view the full dashboard overview
 * 2. **Project Context**: When a project is selected, all features operate within that project's context
 * 3. **Full Overview**: Users can skip project selection to see aggregated data across all projects
 * 4. **Project Management**: Projects can be created and managed through a modal interface
 * 
 * Features available:
 * - Overview: Statistics and quick actions (project-specific or global)
 * - Resume Analysis: Single/bulk analysis and history (filtered by project when selected)
 * - Job Generator: Smart-powered job description creation
 * - Skills Assessment: Custom candidate testing
 * - Interview Management: Interview scheduling and candidate assessment
 * 
 * Header Features:
 * - Team Management: Add and manage team members (admin-only, in header dropdown)
 * - MAMI: AI-powered market insights overlay
 * 
 * Navigation:
 * - Keyboard shortcuts: ⌘1-5 for tabs, ⌘N for new analysis, ⌘R to refresh, ⌘P for project manager
 * - URL-based deep linking with project context preservation
 * - Real-time stats updates and smooth transitions
 */
function DashboardContent() {
  const { user, loading: authLoading } = useAuth();
  const { hasOrganization, isOnboarding, loading: organizationLoading } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [error] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [analysisSubTab, setAnalysisSubTab] = useState('new-analysis');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Project-related state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showMarketIntelligence, setShowMarketIntelligence] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  
  // User name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [userName, setUserName] = useState('');
  
  // Hooks for data fetching - will handle their own loading states
  const { jobDescriptions, isLoading: jobDescriptionsLoading } = useJobDescriptions();
  const { assessments: skillsAssessments } = useSkillsAssessments();
  const { trackFeatureUsage, hasUsedFeature } = useUserActions();
  
  // State for analyses from Supabase
  const [analyses, setAnalyses] = useState<any[]>([]);

  // Initialize user name
  useEffect(() => {
    if (user) {
      const name = user.user_metadata?.display_name || 
                   user.user_metadata?.full_name?.split(' ')[0] || 
                   user.email?.split('@')[0] || 'User';
      setUserName(name);
      setEditedName(name);
    }
  }, [user]);

  // Handle name editing
  const handleNameEdit = () => {
    setIsEditingName(true);
    setEditedName(userName);
  };

  const handleNameSave = async () => {
    if (editedName.trim() && editedName !== userName) {
      try {
        // Update user metadata in Supabase
        const { error } = await supabase.auth.updateUser({
          data: {
            ...user?.user_metadata,
            display_name: editedName.trim()
          }
        });

        if (error) throw error;

        setUserName(editedName.trim());
        addToast('Name updated successfully', 'success');
      } catch (error) {
        console.error('Error updating name:', error);
        addToast('Failed to update name', 'error');
      }
    }
    setIsEditingName(false);
  };

  const handleNameCancel = () => {
    setEditedName(userName);
    setIsEditingName(false);
  };

  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  // Load projects from Supabase - only when not onboarding
  useEffect(() => {
    if (isOnboarding || !user || !hasOrganization) {
      setProjects([]);
      return;
    }
    
    const loadProjects = async () => {
      try {
        const { projectsService } = await import('../../services/projects');
        const projectsData = await projectsService.getProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects([]);
      }
    };
    
    loadProjects();
  }, [lastRefresh, isOnboarding, user, hasOrganization]);

  // Load analyses from Supabase - only when not onboarding
  useEffect(() => {
    if (isOnboarding || !user || !hasOrganization) {
      setAnalyses([]);
      return;
    }
    
    const fetchAnalyses = async () => {
      try {
        const analysesData = await listAnalyses();
        setAnalyses(analysesData);
      } catch (error) {
        console.error('Error loading analyses:', error);
        setAnalyses([]);
      }
    };
    
    fetchAnalyses();
  }, [lastRefresh, user, isOnboarding, hasOrganization]);

  // Handle URL-based navigation and deep links
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const subTab = searchParams.get('subTab');
    const projectId = searchParams.get('project');
    
    if (projectId) {
      setSelectedProjectId(projectId);
    }
    
    if (tab && ['overview', 'projects', 'job-generator', 'market-intelligence', 'analysis'].includes(tab)) {
      setActiveTab(tab);
    }
    
    if (subTab && tab === 'analysis' && ['new-analysis', 'bulk-analysis', 'history'].includes(subTab)) {
      setAnalysisSubTab(subTab);
    }
  }, [location.search]);

  // Keyboard shortcuts for power users
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key >= '1' && event.key <= '5') {
        event.preventDefault();
        const tabs = ['overview', 'analysis', 'job-generator', 'skills-assessment', 'interview-management'];
        const tabIndex = parseInt(event.key) - 1;
        if (tabs[tabIndex]) {
          handleQuickAction(tabs[tabIndex]);
          addToast(`Switched to ${tabs[tabIndex].replace('-', ' ')} tab`, 'info');
        }
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'r') {
        event.preventDefault();
        handleRefreshData();
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'n') {
        event.preventDefault();
        handleQuickAction('analysis', 'new-analysis');
        addToast('Opening new analysis', 'info');
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        event.preventDefault();
        setShowProjectManager(true);
        addToast('Opening project manager', 'info');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [addToast]);

  // Update URL when tabs change
  const updateURL = useCallback((tab: string, subTab?: string, projectId?: string | null) => {
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (subTab) {
      params.set('subTab', subTab);
    }
    if (projectId) {
      params.set('project', projectId);
    }
    navigate(`?${params.toString()}`, { replace: true });
  }, [navigate]);

  // Handle data refresh
  const handleRefreshData = useCallback(() => {
    setLastRefresh(new Date());
    window.dispatchEvent(new CustomEvent('dashboardRefresh'));
    addToast('Dashboard data refreshed', 'success');
  }, [addToast]);

  // Calculate dynamic stats with real-time updates and project isolation
  const dashboardStats = useMemo(() => {
    try {
      // Use projects from state (loaded from Supabase)
      const allProjects = projects;
      
      // Get interviews data (stored in Supabase, so for now we'll use empty array as placeholder)
      // TODO: Fetch interviews from Supabase when needed
      const interviews: any[] = [];
      
      if (selectedProjectId) {
        // Project-specific stats - only show data for selected project
        const currentProject = allProjects.find((p: any) => p.id === selectedProjectId);
        
        // Filter job descriptions by project if selected
        const filteredJobDescriptions = jobDescriptions.filter((jd: any) => jd.project_id === selectedProjectId);
        
        // For project-specific analysis stats, we'll use 0 here since the proper
        // project analysis stats are handled by the DashboardStats component.
        // The analyses array doesn't have direct project_id relationships - they go through project_candidates table
        const filteredAnalyses: any[] = [];
        
        const filteredInterviews = interviews.filter((i: any) => i.project_id === selectedProjectId);
        const filteredSkillsAssessments = skillsAssessments.filter((s: any) => s.project_id === selectedProjectId);
        
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const recentAnalyses = filteredAnalyses.filter((a: any) => 
          new Date(a.created_at || a.createdAt || 0) > oneWeekAgo
        ).length;
        
        return {
          activeProjects: currentProject?.status === 'active' ? 1 : 0,
          totalProjects: 1, // Only showing current project
          totalAnalyses: filteredAnalyses.length,
          recentAnalyses,
          totalInterviews: filteredInterviews.length,
          totalSkillsAssessments: filteredSkillsAssessments.length,
          totalJobDescriptions: filteredJobDescriptions.length,
          companiesTracked: currentProject?.company ? 1 : 0,
          isProjectSpecific: true
        };
      } else {
        // Full dashboard stats - show aggregated data from all projects
        const activeProjects = allProjects.filter((p: any) => p.status === 'active').length;
        const totalProjects = allProjects.length;
        const totalAnalyses = analyses.length;
        const totalInterviews = interviews.length;
        const totalSkillsAssessments = skillsAssessments.length;
        
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const recentAnalyses = analyses.filter((a: any) => 
          new Date(a.created_at || a.createdAt || 0) > oneWeekAgo
        ).length;
        
        const companiesFromProjects = new Set(allProjects.map((p: any) => p.company).filter(Boolean));
        const companiesTracked = companiesFromProjects.size;
        
        return {
          activeProjects,
          totalProjects,
          totalAnalyses,
          recentAnalyses,
          totalInterviews,
          totalSkillsAssessments,
          totalJobDescriptions: jobDescriptions.length,
          companiesTracked,
          isProjectSpecific: false
        };
      }
    } catch (error) {
      console.error('Error calculating dashboard stats:', error);
      return {
        activeProjects: 0,
        totalProjects: 0,
        totalAnalyses: 0,
        recentAnalyses: 0,
        totalInterviews: 0,
        totalSkillsAssessments: 0,
        totalJobDescriptions: 0,
        companiesTracked: 0,
        isProjectSpecific: !!selectedProjectId
      };
    }
  }, [lastRefresh, selectedProjectId, jobDescriptions, analyses, skillsAssessments, projects]);

  // Get selected project details
  const selectedProject = useMemo(() => {
    return selectedProjectId ? projects.find(p => p.id === selectedProjectId) : null;
  }, [selectedProjectId, projects]);

  // Quick action handlers with enhanced UX
  const handleQuickAction = useCallback((action: string, subAction?: string) => {
    setIsTransitioning(true);
    
    setTimeout(async () => {
      setActiveTab(action);
      updateURL(action, subAction, selectedProjectId);
      
      if (subAction) {
        setAnalysisSubTab(subAction);
      }
      
      setIsTransitioning(false);
      
      const hasUsedAction = hasUsedFeature(action);
      if (!hasUsedAction) {
        const actionMessages = {
          'job-generator': 'Generate professional job descriptions with Smart technology to attract candidates.',
          'analysis': 'Upload resumes to get detailed candidate analysis and recommendations.',
          'bulk-analysis': 'Upload multiple CVs for efficient batch analysis and candidate comparison.',
          'market-intelligence': 'Explore salary trends and market insights for better hiring decisions.'
        };
        
        if (actionMessages[action as keyof typeof actionMessages]) {
          addToast(actionMessages[action as keyof typeof actionMessages], 'info');
          try {
            await trackFeatureUsage(action, { 
              sub_action: subAction, 
              project_id: selectedProjectId 
            });
          } catch (error) {
            console.error('Error tracking feature usage:', error);
          }
        }
      }
    }, 150);
  }, [updateURL, addToast, selectedProjectId, hasUsedFeature, trackFeatureUsage]);

  // Handle project selection
  const handleProjectSelect = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
    updateURL(activeTab, undefined, projectId);
    if (projectId) {
      const project = projects.find(p => p.id === projectId);
      addToast(`Switched to project: ${project?.title || 'Unknown'}`, 'success');
    } else {
      addToast('Viewing full dashboard overview', 'info');
    }
  }, [activeTab, updateURL, addToast, projects]);

  // Enhanced loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200">
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-5 w-96" />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
          <div className="space-y-8">
            <Skeleton className="h-16 w-full rounded-xl" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))}
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
              <div className="xl:col-span-3">
                <Skeleton className="h-96 rounded-xl" />
              </div>
              <Skeleton className="h-96 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
     return (
       <Alert variant="destructive" className="mb-6">
         <Terminal className="h-4 w-4" />
         <AlertTitle>Error</AlertTitle>
         <AlertDescription>{error}</AlertDescription>
       </Alert>
     );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-6">
            <div className="h-16 w-16 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">RM</span>
            </div>
            <CardTitle className="text-xl text-slate-900">Welcome to Partner Plus Dashboard</CardTitle>
            <p className="text-slate-600 mt-2">Please sign in to access your dashboard and start your hiring journey.</p>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full bg-slate-900 hover:bg-slate-800">
              Sign In to Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while checking organization membership
  if (organizationLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading your organization...</p>
        </div>
      </div>
    );
  }

  // This should not happen as routing handles redirecting users without organizations
  if (!hasOrganization && !organizationLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Redirecting to onboarding...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50">
      {/* Dashboard Header */}
      <DashboardHeader />
      
      {/* Project Selector Section */}
      <div className="bg-white border-b border-slate-200">
        <div className="w-full px-6 lg:px-8 xl:px-12 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              {/* Dashboard Title Card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300 px-6 py-4 flex-1">
                <div className="flex items-center gap-4">
                  {/* Title Content */}
                  <div className="flex-1 min-w-0">
                    {selectedProject ? (
                      <div className="space-y-1">
                        <h1 className="text-2xl lg:text-3xl font-medium text-slate-900 tracking-tight font-sans antialiased leading-tight">
                          {selectedProject.title}
                        </h1>
                        <p className="text-slate-600 font-medium">
                          {selectedProject.company} • {selectedProject.role}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-3 group">
                          {isEditingName ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                type="text"
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                onKeyDown={handleNameKeyPress}
                                onBlur={handleNameSave}
                                className="text-2xl lg:text-3xl font-medium text-slate-900 tracking-tight font-sans antialiased bg-transparent border-0 border-b-2 border-slate-300 focus:border-slate-600 focus:ring-0 outline-none px-1 py-1 min-w-0 flex-1 rounded-none shadow-none leading-tight"
                                autoFocus
                                maxLength={20}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleNameSave}
                                className="h-8 w-8 p-0 opacity-70 hover:opacity-100"
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleNameCancel}
                                className="h-8 w-8 p-0 opacity-70 hover:opacity-100"
                              >
                                <X className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <h1 className="text-2xl lg:text-3xl font-medium text-slate-900 tracking-tight font-sans antialiased selection:bg-slate-200 leading-tight">
                                {userName}'s Dashboard
                              </h1>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleNameEdit}
                                className="h-8 w-8 p-0 opacity-0 group-hover:opacity-70 hover:opacity-100 transition-all duration-200 hover:bg-slate-100 rounded-lg"
                              >
                                <Pencil className="h-4 w-4 text-slate-500" />
                              </Button>
                            </>
                          )}
                        </div>
                        <p className="text-slate-500 text-sm font-medium">
                          Welcome back! Ready to find your next great hire?
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Status Indicator */}
                  <div className="hidden lg:flex items-center gap-2">
                    <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-slate-600">Active</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Project Selector */}
              <div className="flex items-center gap-3">
                <Select
                  value={selectedProjectId || "overview"}
                  onValueChange={(value) => {
                    if (value === "overview") {
                      handleProjectSelect(null);
                    } else if (value === "manage") {
                      setShowProjectManager(true);
                    } else {
                      handleProjectSelect(value);
                    }
                  }}
                >
                  <SelectTrigger className="w-64 bg-white border-slate-200 hover:border-slate-300">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overview">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-4 w-4" />
                        <span>Full Dashboard Overview</span>
                      </div>
                    </SelectItem>
                    {projects.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Projects
                        </div>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            <div className="flex items-center gap-3">
                              <div className={`h-2 w-2 rounded-full ${
                                project.status === 'active' ? 'bg-green-500' : 'bg-slate-400'
                              }`} />
                              <span className="truncate">{project.title}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    <div className="border-t border-slate-200 mt-1 pt-1">
                      <SelectItem value="manage">
                        <div className="flex items-center gap-3">
                          <Plus className="h-4 w-4" />
                          <span>Manage Projects</span>
                        </div>
                      </SelectItem>
                    </div>
                  </SelectContent>
                </Select>

                {selectedProject && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleProjectSelect(null)}
                    className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Overview
                  </Button>
                )}
              </div>

              {/* Stats and Actions */}
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowMarketIntelligence(!showMarketIntelligence)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">MAMI</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-6 lg:px-8 xl:px-12 py-8">
        {/* Enhanced Project Manager Modal */}
        <ProjectModal 
          isOpen={showProjectManager}
          onClose={() => setShowProjectManager(false)}
        />

        {/* MAMI Overlay */}
        <MarketIntelligenceOverlay 
          isOpen={showMarketIntelligence}
          onClose={() => setShowMarketIntelligence(false)}
        />

        <Tabs value={activeTab} onValueChange={(value) => {
          handleQuickAction(value);
        }} className="space-y-8">
          {/* Enhanced Navigation Tabs */}
          <div className={`bg-white rounded-xl p-2 shadow-sm border border-slate-200 transition-all duration-300 ${isTransitioning ? 'opacity-60 scale-[0.99]' : 'opacity-100 scale-100'}`}>
            <TabsList className="grid w-full grid-cols-5 bg-transparent gap-1">
              <TabsTrigger 
                value="overview" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <BarChart3 className="h-4 w-4" />
                <span className="font-medium text-sm">
                  {selectedProject ? 'Project Overview' : 'Overview'}
                </span>
              </TabsTrigger>
              <TabsTrigger 
                value="analysis" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <FileTextIcon className="h-4 w-4" />
                <span className="font-medium text-sm">Resume Analysis</span>
              </TabsTrigger>
              <TabsTrigger 
                value="job-generator" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <FileEdit className="h-4 w-4" />
                <span className="font-medium text-sm">Job Generator</span>
              </TabsTrigger>
              <TabsTrigger 
                value="skills-assessment" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <Brain className="h-4 w-4" />
                <span className="font-medium text-sm">Skills Assessment</span>
              </TabsTrigger>
              <TabsTrigger 
                value="interview-management" 
                className="flex items-center gap-3 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium text-sm">Interview Management</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
              {/* Professional Stats Cards */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => setShowProjectManager(true)}>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <p className="text-slate-600 text-sm font-medium">
                      {selectedProject ? 'Project Status' : 'Active Projects'}
                    </p>
                    <p className="text-3xl font-bold text-slate-900">
                      {selectedProject 
                        ? (selectedProject.status === 'active' ? 'Active' : 'Inactive')
                        : dashboardStats.activeProjects
                      }
                    </p>
                    <p className="text-slate-500 text-xs">
                      {selectedProject 
                        ? `${selectedProject.company} • ${selectedProject.role}`
                        : `${dashboardStats.totalProjects} total projects`
                      }
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <Target className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span>{selectedProject ? 'Manage project' : 'View all projects'}</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => handleQuickAction('analysis', 'history')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <p className="text-slate-600 text-sm font-medium">Resumes Analyzed</p>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.totalAnalyses}</p>
                    <p className="text-slate-500 text-xs">
                      {dashboardStats.recentAnalyses} this week
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <FileTextIcon className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span>View analysis history</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>

              {/* Skills Assessment */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => handleQuickAction('skills-assessment')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <p className="text-slate-600 text-sm font-medium">Skills Assessment</p>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.totalSkillsAssessments}</p>
                    <p className="text-slate-500 text-xs">
                      {selectedProject ? 'For this project' : 'Create assessments'}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <Brain className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span>{selectedProject ? 'View assessments' : 'Create test'}</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>

              {/* Interview Management */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => handleQuickAction('interview-management')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <p className="text-slate-600 text-sm font-medium">Interview Management</p>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.totalInterviews}</p>
                    <p className="text-slate-500 text-xs">
                      {selectedProject ? 'Schedule interviews' : 'Manage interviews'}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span>{selectedProject ? 'Start interview' : 'View interviews'}</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>

              {/* Additional stats card for wider screens */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer xl:block hidden"
                   onClick={() => handleQuickAction('job-generator')}>
                <div className="flex items-center justify-between mb-4">
                  <div className="space-y-1">
                    <p className="text-slate-600 text-sm font-medium">Job Descriptions</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {jobDescriptionsLoading ? (
                        <Skeleton className="h-8 w-12" />
                      ) : (
                        dashboardStats.totalJobDescriptions
                      )}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {dashboardStats.totalJobDescriptions === 0 ? 'Generate with Smart' : `${jobDescriptions.filter(jd => new Date(jd.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length} created this week`}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <FileEdit className="h-6 w-6 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <span>{dashboardStats.totalJobDescriptions === 0 ? 'Create description' : 'View descriptions'}</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </div>

            {/* Workflow Helper - Only show in full dashboard view */}
            {!selectedProject && (
              <div className="animate-fade-in">
                <DashboardWorkflowHelper 
                  onNavigateToTab={(tab, subTab) => {
                    handleQuickAction(tab, subTab);
                  }}
                />
              </div>
            )}

            {/* Project-Specific Context Message */}
            {selectedProject && (
              <div className="animate-fade-in">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                      <FolderOpen className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-1">
                        Working on: {selectedProject.title}
                      </h3>
                      <p className="text-slate-600">
                        All data and features are now filtered to show only information related to this project. 
                        Switch to "Full Dashboard Overview" to see aggregated data across all projects.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`px-3 py-1 ${
                        selectedProject.status === 'active' 
                          ? 'bg-green-50 text-green-700 border-green-200' 
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}>
                        {selectedProject.status}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleProjectSelect(null)}
                        className="text-slate-600 hover:text-slate-900 border-slate-200"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Full Overview
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions Layout */}
            <div className="w-full">
                <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
                  {/* Time Saved Metrics - Compact & Fun */}
                  <div className="p-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 rounded-2xl border border-emerald-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-emerald-800 group-hover:text-emerald-900 transition-colors">
                          ⚡ Time saved with MAMI
                        </h3>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors">
                          {(() => {
                            const totalMinutes = (dashboardStats.totalAnalyses * 7) + (dashboardStats.totalJobDescriptions * 20) + (dashboardStats.totalSkillsAssessments * 20);
                            return `${Math.round(totalMinutes / 60)}h ${totalMinutes % 60}m`;
                          })()}
                        </div>
                        <p className="text-xs text-emerald-600 font-medium">🎉 Total Saved</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center">
                        <Target className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">Quick Actions</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-4">
                      <button 
                        onClick={() => handleQuickAction('projects')}
                        className="group p-6 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-slate-900 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <Target className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">New Project</h4>
                            <p className="text-xs text-slate-600">Start hiring project</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('job-generator')}
                        className="group p-6 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-slate-900 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <FileEdit className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">Job Description</h4>
                            <p className="text-xs text-slate-600">Generate with Smart</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('analysis', 'new-analysis')}
                        className="group p-6 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-slate-900 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <FileTextIcon className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">Analyze Resume</h4>
                            <p className="text-xs text-slate-600">Single upload</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('analysis', 'bulk-analysis')}
                        className="group p-6 bg-gradient-to-br from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <Users className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">Bulk Analysis</h4>
                            <p className="text-xs text-slate-600">Multiple CVs</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('interview-management')}
                        className="group p-6 bg-gradient-to-br from-green-50 to-teal-50 hover:from-green-100 hover:to-teal-100 rounded-xl border border-green-200 hover:border-green-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-gradient-to-br from-green-600 to-teal-600 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <Users className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">Interview Management</h4>
                            <p className="text-xs text-slate-600">Schedule & conduct</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('analysis', 'history')}
                        className="group p-6 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-slate-900 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <History className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">View History</h4>
                            <p className="text-xs text-slate-600">Recent analyses</p>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleQuickAction('skills-assessment')}
                        className="group p-6 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all duration-200 text-left"
                      >
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-lg group-hover:scale-110 transition-transform">
                            <Brain className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-center space-y-1">
                            <h4 className="font-semibold text-slate-900 text-sm">Skills Assessment</h4>
                            <p className="text-xs text-slate-600">Custom tests</p>
                          </div>
                        </div>
                      </button>


                    </div>
                  </div>

                  {/* Recent Activity Cards - Show filtered data based on project selection */}
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Recent Analysis Activity */}
                        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-3 text-lg">
                              <div className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-bold text-slate-900">
                                {selectedProject ? 'Project Activity' : 'Recent Analysis'}
                              </span>
                            </CardTitle>
                            {selectedProject && (
                              <p className="text-sm text-slate-600 mt-2">
                                Activity for {selectedProject.title}
                              </p>
                            )}
                          </CardHeader>
                          <CardContent>
                            <RecentActivity 
                              projectId={selectedProjectId}
                              onNavigateToAnalysis={() => handleQuickAction('analysis', 'new-analysis')}
                              onNavigateToHistory={() => handleQuickAction('analysis', 'history')}
                            />
                          </CardContent>
                        </Card>

                        {/* Recent Interviews */}
                        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-3 text-lg">
                              <div className="h-8 w-8 rounded-lg bg-green-600 flex items-center justify-center">
                                <Users className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-bold text-slate-900">Recent Interviews</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <RecentInterviews 
                              onNavigateToInterviews={() => handleQuickAction('interview-management')}
                              limit={3}
                              projectId={selectedProjectId}
                            />
                          </CardContent>
                        </Card>

                        {/* Recent Skills Assessments */}
                        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-3 text-lg">
                              <div className="h-8 w-8 rounded-lg bg-orange-600 flex items-center justify-center">
                                <Brain className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-bold text-slate-900">Skills Assessments</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <RecentSkillsAssessments 
                              onNavigateToSkillsAssessments={() => handleQuickAction('skills-assessment')}
                              limit={3}
                              projectId={selectedProjectId}
                            />
                          </CardContent>
                        </Card>

                        {/* Recent Job Descriptions */}
                        <Card className="bg-white shadow-sm border border-slate-200 rounded-xl">
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-3 text-lg">
                              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                <FileEdit className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-bold text-slate-900">Job Descriptions</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <RecentJobDescriptions 
                              onNavigateToGenerator={() => handleQuickAction('job-generator')}
                              limit={3}
                            />
                          </CardContent>
                        </Card>
                    </div>
                  </div>

                </div>
            </div>
          </TabsContent>

          {/* Resume Analysis Tab with Sub-tabs */}
          <TabsContent value="analysis" className="space-y-8">
            <div className="animate-fade-in">
              <Tabs value={analysisSubTab} onValueChange={setAnalysisSubTab} className="space-y-6">
                <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-200">
                  <TabsList className="grid w-full grid-cols-3 bg-transparent gap-1">
                    <TabsTrigger value="new-analysis" className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                      <Upload className="h-4 w-4" />
                      <span className="font-medium">Single Analysis</span>
                    </TabsTrigger>
                    <TabsTrigger value="bulk-analysis" className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">Bulk Analysis</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                      <History className="h-4 w-4" />
                      <span className="font-medium">Analysis History</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="new-analysis">
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-8">
                      <UploadForm />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="bulk-analysis">
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-8">
                      <BulkAnalysisPage />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="history">
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-8">
                      <AnalysisHistory />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          {/* Job Description Generator Tab */}
          <TabsContent value="job-generator" className="space-y-0">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-8">
                <JobDescriptionGenerator />
              </div>
            </div>
          </TabsContent>

          {/* Smart Skills Assessment Tab */}
          <TabsContent value="skills-assessment" className="space-y-8">
            <div className="animate-fade-in">
              <SkillsAssessment />
            </div>
          </TabsContent>

          {/* Interview Management Tab */}
          <TabsContent value="interview-management" className="space-y-0">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-8">
                <InterviewDashboard />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* GDPR Compliance Section */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <GDPRCompliance variant="dashboard" />
        </div>
      </div>
      
      {/* Floating GDPR Badges */}
      <GDPRCompliance variant="floating" />
    </div>
  );
}

export function Dashboard() {
  return (
    <DashboardErrorBoundary>
      <DashboardContent />
    </DashboardErrorBoundary>
  );
}